import { ApiSchema } from "@/modules/api";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { BaseSyncOperationGuestMode } from "@/store/sync/operations/BaseSyncOperationGuestMode";
import { BaseSyncOperationGeneric } from "@/store/sync/operations/BaseSyncOperationGeneric";
import { GenericSyncOperationKind } from "@/store/sync/types";
import { MaybeNull } from "@/domains/common/types";
import { MemCommonEditorFileInfo, MemCommonEditorImageInfo } from "@mem-labs/common-editor";
import { BaseExternalOperation } from "@/store/sync/operations/BaseExternalOperation";
import { Uuid } from "@/domains/global/identifiers";
import type {
  AssistantFollowUpStatus,
  AssistantProjectStatus,
  AssistantRoutineStatus,
  AssistantSkillOrigin,
  AssistantSkillStatus,
  AssistantTaskStatus,
} from "@/store/assistant/types";
import type { SpaceAccountSettingsPatch } from "@/store/space-account-settings/types";

type ApiSchemaValue<Key extends PropertyKey> = Key extends keyof ApiSchema ? ApiSchema[Key] : never;

export type AudioRecordingMergeIntentPayload = {
  canonical_audio_recording_id: Uuid;
  source_audio_recording_ids: Uuid[];
};

// NOTE OPERATIONS
export type ICreateNoteOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateNoteSyncOperationPayloadRequest"];
export type IUpdateNoteContentUsingDiffOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateNoteContentUsingDiffSyncOperationPayloadRequest"];
export type IUpdateNoteContentUsingFullStateOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateNoteContentUsingFullStateSyncOperationPayloadRequest"];
export type IUpdateNoteContentOverwritingFullStateDestructivelyOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateNoteContentOverwritingFullStateDestructivelySyncOperationPayloadRequest"];
export type IDeleteNoteOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelDeleteNoteSyncOperationPayloadRequest"];
export type ITrashNoteOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelTrashNoteSyncOperationPayloadRequest"];
export type IRestoreNoteOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelRestoreNoteSyncOperationPayloadRequest"];
export type IUpdateNoteLocallyCreatedAtOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateNoteLocallyCreatedAtSyncOperationPayloadRequest"];
export type IUpdateNoteIsEditableByAiOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateNoteIsEditableByAiSyncOperationPayloadRequest"];

// NOTE SUGGESTION OPERATIONS
export type IAcceptNoteSuggestionOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelAcceptNoteSuggestionSyncOperationPayloadRequest"];
export type IRejectNoteSuggestionOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelRejectNoteSuggestionSyncOperationPayloadRequest"];
type GeneratedUpsertNoteSuggestionOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpsertNoteSuggestionSyncOperationPayloadRequest"];
export type IUpdateNoteReminderShillOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateNoteReminderShillSyncOperationPayloadRequest"];
export type UpsertNoteSuggestionAudioRecordingMergeIntentPayload = AudioRecordingMergeIntentPayload;
export type IUpsertNoteSuggestionOperation = GeneratedUpsertNoteSuggestionOperation & {
  payload: GeneratedUpsertNoteSuggestionOperation["payload"] & {
    audio_recording_merge_intent?: UpsertNoteSuggestionAudioRecordingMergeIntentPayload | null;
  };
};
export type AudioUploadMergeIntentPayload = AudioRecordingMergeIntentPayload;

// NOTE + TEMPLATE OPERATIONS
export type ICreateNoteUsingTemplateOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateNoteUsingTemplateSyncOperationPayloadRequest"];
export type IUpdateNoteContentUsingTemplateAndDiffOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateNoteContentUsingTemplateAndDiffSyncOperationPayloadRequest"];

// NOTE + CALENDAR EVENT OPERATIONS
export type ICreateNoteUsingCalendarEventOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateNoteUsingCalendarEventSyncOperationPayloadRequest"];
export type ILinkNoteToCalendarEventOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelLinkNoteToCalendarEventSyncOperationPayloadRequest"];

// CALENDAR EVENT OPERATIONS
export type IUpdateSpaceAccountCalendarEventOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateSpaceAccountCalendarEventSyncOperationPayloadRequest"];

// NOTE ACL OPERATIONS
export type IGrantNoteAclViaSpaceAccountOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelGrantNoteAclViaSpaceAccountSyncOperationPayloadRequest"];
export type IGrantNoteAclViaEmailAddressOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelGrantNoteAclViaEmailAddressSyncOperationPayloadRequest"];
export type IUpdateNoteAclViaSpaceAccountOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateNoteAclViaSpaceAccountSyncOperationPayloadRequest"];
export type IUpdateNoteAclViaEmailAddressOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateNoteAclViaEmailAddressSyncOperationPayloadRequest"];
export type IUpdateNoteAclViaCollectionOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateNoteAclViaCollectionSyncOperationPayloadRequest"];
export type IRevokeNoteAclViaSpaceAccountOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelRevokeNoteAclViaSpaceAccountSyncOperationPayloadRequest"];
export type IRevokeNoteAclViaEmailAddressOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelRevokeNoteAclViaEmailAddressSyncOperationPayloadRequest"];

// COLLECTION OPERATIONS
export type ICreateCollectionOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateCollectionSyncOperationPayloadRequest"];
export type IUpdateCollectionOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateCollectionSyncOperationPayloadRequest"];
export type IDeleteCollectionOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelDeleteCollectionSyncOperationPayloadRequest"];
export type IAddNoteToCollectionOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelAddNoteToCollectionSyncOperationPayloadRequest"];
export type IRemoveNoteFromCollectionOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelRemoveNoteFromCollectionSyncOperationPayloadRequest"];

// COLLECTION ACL OPERATIONS
export type IGrantCollectionAclViaSpaceAccountOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelGrantCollectionAclViaSpaceAccountSyncOperationPayloadRequest"];
export type IGrantCollectionAclViaEmailAddressOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelGrantCollectionAclViaEmailAddressSyncOperationPayloadRequest"];
export type IUpdateCollectionAclViaSpaceAccountOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateCollectionAclViaSpaceAccountSyncOperationPayloadRequest"];
export type IUpdateCollectionAclViaEmailAddressOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateCollectionAclViaEmailAddressSyncOperationPayloadRequest"];
export type IRevokeCollectionAclViaSpaceAccountOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelRevokeCollectionAclViaSpaceAccountSyncOperationPayloadRequest"];
export type IRevokeCollectionAclViaEmailAddressOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelRevokeCollectionAclViaEmailAddressSyncOperationPayloadRequest"];
export type IMakeCollectionPrivateOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelMakeCollectionPrivateSyncOperationPayloadRequest"];

// FAVORITES OPERATIONS
export type IAddNoteToFavoritesOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelAddNoteToFavoritesSyncOperationPayloadRequest"];
export type IAddCollectionToFavoritesOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelAddCollectionToFavoritesSyncOperationPayloadRequest"];
export type IAddSavedSearchToFavoritesOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelAddSavedSearchToFavoritesSyncOperationPayloadRequest"];
export type IRemoveNoteFromFavoritesOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelRemoveNoteFromFavoritesSyncOperationPayloadRequest"];
export type IRemoveCollectionFromFavoritesOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelRemoveCollectionFromFavoritesSyncOperationPayloadRequest"];
export type IRemoveSavedSearchFromFavoritesOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelRemoveSavedSearchFromFavoritesSyncOperationPayloadRequest"];
export type IUpdateFavoriteItemSortKeyOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateFavoriteItemSortKeySyncOperationPayloadRequest"];

// RECENTS OPERATIONS
export type IMarkNoteViewedOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelMarkNoteViewedSyncOperationPayloadRequest"];
export type IMarkCollectionViewedOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelMarkCollectionViewedSyncOperationPayloadRequest"];

// FEEDBACK OPERATIONS
export type IProvideFeedbackOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelProvideFeedbackSyncOperationPayloadRequest"];

// APP CALLOUT OPERATIONS
export type IDismissSpaceAccountAppCalloutOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelDismissSpaceAccountAppCalloutSyncOperationPayloadRequest"];
export type IEnableSpaceAccountAppCalloutDismissalOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelEnableSpaceAccountAppCalloutDismissalSyncOperationPayloadRequest"];

// API KEY OPERATIONS
export type ICreateApiKeyOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateApiKeySyncOperationPayloadRequest"];
export type IUpdateApiKeyOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateApiKeySyncOperationPayloadRequest"];
export type IDeleteApiKeyOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelDeleteApiKeySyncOperationPayloadRequest"];

// AUDIO RECORDING OPERATIONS
export type ICreateAudioRecordingOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateAudioRecordingSyncOperationPayloadRequest"];
export type IUpdateAudioRecordingOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateAudioRecordingSyncOperationPayloadRequest"];
export type IDeleteAudioRecordingOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelDeleteAudioRecordingSyncOperationPayloadRequest"];
export type ITrashAudioRecordingOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelTrashAudioRecordingSyncOperationPayloadRequest"];
export type IRestoreAudioRecordingOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelRestoreAudioRecordingSyncOperationPayloadRequest"];

// AUDIO RECORDING APP EVENT OPERATIONS
export type IAudioRecordingAppEventOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelBaseAudioRecordingAppEventSyncOperationPayloadRequest"];

// Narrowed type for the specific audio recording app event operation kinds
export type AudioRecordingAppEventOperationKind = Extract<
  IAudioRecordingAppEventOperation["operation_kind"],
  | "MARK_STARTED_LOCAL_AUDIO_RECORDING"
  | "MARK_PAUSED_LOCAL_AUDIO_RECORDING"
  | "MARK_RESUMED_LOCAL_AUDIO_RECORDING"
  | "MARK_COMPLETED_LOCAL_AUDIO_RECORDING"
  | "MARK_ERROR_STARTING_LOCAL_AUDIO_RECORDING"
  | "MARK_ERROR_RESUMING_LOCAL_AUDIO_RECORDING"
  | "MARK_STARTED_UPLOADING_AUDIO_RECORDING"
  | "MARK_COMPLETED_UPLOADING_AUDIO_RECORDING"
  | "MARK_SKIPPED_UPLOADING_AUDIO_RECORDING"
  | "MARK_ERROR_UPLOADING_AUDIO_RECORDING"
  | "MARK_TRANSCRIPTION_DISCONNECTED_AUDIO_RECORDING"
  | "MARK_TRANSCRIPTION_RECONNECTED_AUDIO_RECORDING"
  | "MARK_TRANSCRIPTION_COMPROMISED_AUDIO_RECORDING"
  | "MARK_VOICE_DISCONNECTED_AUDIO_RECORDING"
  | "MARK_VOICE_RECONNECTED_AUDIO_RECORDING"
  | "MARK_STARTED_PROCESSING_AUDIO_RECORDING"
  | "MARK_ERROR_PROCESSING_AUDIO_RECORDING"
  | "MARK_COMPLETED_PROCESSING_AUDIO_RECORDING"
  | "MARK_STARTED_RETRANSCRIBING_AUDIO_RECORDING"
  | "MARK_COMPLETED_RETRANSCRIBING_AUDIO_RECORDING"
  | "MARK_ERROR_RETRANSCRIBING_AUDIO_RECORDING"
  | "MARK_UNEXPECTED_ERROR_AUDIO_RECORDING"
>;

// TEMPLATE OPERATIONS
export type ICreateTemplateOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateTemplateSyncOperationPayloadRequest"];
export type IUpdateTemplateOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateTemplateSyncOperationPayloadRequest"];
export type IUpdateTemplateContentUsingDiffOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateTemplateContentUsingDiffSyncOperationPayloadRequest"];
export type IDeleteTemplateOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelDeleteTemplateSyncOperationPayloadRequest"];
export type IMarkTemplateViewedOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelMarkTemplateViewedSyncOperationPayloadRequest"];
export type IMarkTemplateUsedOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelMarkTemplateUsedSyncOperationPayloadRequest"];

// TEMPLATE ACL OPERATIONS
export type IGrantTemplateAclViaEmailAddressOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelGrantTemplateAclViaEmailAddressSyncOperationPayloadRequest"];
export type IGrantTemplateAclViaSpaceAccountOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelGrantTemplateAclViaSpaceAccountSyncOperationPayloadRequest"];
export type IUpdateTemplateAclViaSpaceAccountOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateTemplateAclViaSpaceAccountSyncOperationPayloadRequest"];
export type IRevokeTemplateAclViaSpaceAccountOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelRevokeTemplateAclViaSpaceAccountSyncOperationPayloadRequest"];

// TEMPLATE +  COLLECTION OPERATIONS
export type IAddTemplateToCollectionOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelAddTemplateToCollectionSyncOperationPayloadRequest"];
export type IRemoveTemplateFromCollectionOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelRemoveTemplateFromCollectionSyncOperationPayloadRequest"];
export type IUpdateTemplateAclViaCollectionOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateTemplateAclViaCollectionSyncOperationPayloadRequest"];

// DATA EXPORT OPERATIONS
export type ICreateDataExportOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateDataExportSyncOperationPayloadRequest"] & {
    payload: ApiSchema["SyncPushApi__CreateDataExportPayloadSchema"];
  };

// UPLOADED FILE BATCH OPERATIONS
export type ICreateUploadedFileBatchOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateUploadedFileBatchSyncOperationPayloadRequest"];
export type IUpdateUploadedFileBatchOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateUploadedFileBatchSyncOperationPayloadRequest"];
export type ICreateUploadedFileOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateUploadedFileSyncOperationPayloadRequest"];
export type IBatchCreateUploadedFileOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelBatchCreateUploadedFileSyncOperationPayloadRequest"];

// SESSION OPERATIONS
export type ICreateSessionEventOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateSessionEventSyncOperationPayloadRequest"];
export type ICreateSessionOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateSessionSyncOperationPayloadRequest"];
export type IUpdateSessionOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelUpdateSessionSyncOperationPayloadRequest"];
export type IDeleteSessionOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelDeleteSessionSyncOperationPayloadRequest"];
// MARK_SESSION_SEEN exists only in the SyncPushApi contract; the legacy client
// union never gained it, so graft its payload onto the legacy envelope.
export type IMarkSessionSeenOperation = Omit<
  IUpdateSessionOperation,
  "operation_kind" | "payload"
> & {
  operation_kind: "MARK_SESSION_SEEN";
  payload: ApiSchema["SyncPushApi__SubmitMarkSessionSeenRequestSchema"]["payload"];
};

// SESSION EVENT OPERATIONS
export type ICreateMessageSentSessionEventOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateMessageSentSessionEventSyncOperationPayloadRequest"];

// SOURCE OPERATIONS
type CreateSourceOperationBase =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateSourceSyncOperationPayloadRequest"];
export type ICreateSourceOperation = Omit<CreateSourceOperationBase, "payload"> & {
  payload: CreateSourceOperationBase["payload"] & {
    associated_audio_recording_id?: Uuid | null;
  };
};
export type IDeleteSourceOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelDeleteSourceSyncOperationPayloadRequest"];
export type IBatchDeleteSourceOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelBatchDeleteSourceSyncOperationPayloadRequest"];

// NOTE SOURCE OPERATIONS
export type ICreateNoteSourceOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateNoteSourceSyncOperationPayloadRequest"];
export type IDeleteNoteSourceOperation =
  ApiSchema["SubmitSyncOperationRequestSyncModelDeleteNoteSourceSyncOperationPayloadRequest"];

// SPACE ACCOUNT SETTINGS OPERATIONS
type CurrentApiUpdateSpaceAccountSettingsOperation =
  ApiSchemaValue<"SubmitSyncOperationRequestSyncModelUpdateSpaceAccountSettingsPayloadRequest">;
type DeployedApiUpdateSpaceAccountSettingsOperation =
  ApiSchemaValue<"SubmitSyncOperationRequestSyncModelUpdateSpaceAccountSettingsSyncOperationPayloadRequest">;
type ApiUpdateSpaceAccountSettingsOperation = [
  CurrentApiUpdateSpaceAccountSettingsOperation,
] extends [never]
  ? DeployedApiUpdateSpaceAccountSettingsOperation
  : CurrentApiUpdateSpaceAccountSettingsOperation;

export type IUpdateSpaceAccountSettingsOperation = Omit<
  ApiUpdateSpaceAccountSettingsOperation,
  "payload"
> & {
  payload: Omit<ApiUpdateSpaceAccountSettingsOperation["payload"], "settings"> & {
    settings: SpaceAccountSettingsPatch;
  };
};

// ASSISTANT TASK / PROJECT / FOLLOW-UP OPERATIONS
type SubmitSyncOperationRequestShared = ApiSchema["SubmitSyncOperationRequestShared"];
type AssistantSyncOperationRequestShared = Omit<SubmitSyncOperationRequestShared, "operation_kind">;

export type ICreateAssistantTaskOperation = AssistantSyncOperationRequestShared & {
  operation_kind: "CREATE_ASSISTANT_TASK";
  payload: {
    schema_version: 1;
    id: Uuid;
    assistant_id: Uuid;
    title: string;
    description?: string | null;
    status: AssistantTaskStatus;
    due_at?: string | null;
    project_id?: Uuid | null;
    referenced_note_ids?: Uuid[];
  };
};

export type IUpdateAssistantTaskOperation = AssistantSyncOperationRequestShared & {
  operation_kind: "UPDATE_ASSISTANT_TASK";
  payload: {
    schema_version: 1;
    id: Uuid;
    assistant_id?: Uuid | null;
    add_referenced_note_ids?: Uuid[];
    remove_referenced_note_ids?: Uuid[];
    title?: string | null;
    description?: string | null;
    status?: AssistantTaskStatus | null;
    due_at?: string | null;
    clear_due_at?: boolean;
    project_id?: Uuid | null;
    clear_project_id?: boolean;
  };
};

export type ICreateAssistantProjectOperation = AssistantSyncOperationRequestShared & {
  operation_kind: "CREATE_ASSISTANT_PROJECT";
  payload: {
    schema_version: 1;
    id: Uuid;
    assistant_id: Uuid;
    title: string;
    description?: string | null;
    status: AssistantProjectStatus;
  };
};

export type IUpdateAssistantProjectOperation = AssistantSyncOperationRequestShared & {
  operation_kind: "UPDATE_ASSISTANT_PROJECT";
  payload: {
    schema_version: 1;
    id: Uuid;
    title?: string | null;
    description?: string | null;
    status?: AssistantProjectStatus | null;
  };
};

export type ICreateAssistantFollowUpOperation = AssistantSyncOperationRequestShared & {
  operation_kind: "CREATE_ASSISTANT_FOLLOW_UP";
  payload: {
    schema_version: 1;
    id: Uuid;
    assistant_id: Uuid;
    content: string;
    status: AssistantFollowUpStatus;
    scheduled_for: string;
    project_id?: Uuid | null;
    task_id?: Uuid | null;
    trigger_type?: string | null;
    commitment_kind?: string | null;
    due_at?: string | null;
    deadline_at?: string | null;
    trigger_not_before?: string | null;
    trigger_not_after?: string | null;
    source_context_id?: Uuid | null;
  };
};

export type IUpdateAssistantFollowUpOperation = AssistantSyncOperationRequestShared & {
  operation_kind: "UPDATE_ASSISTANT_FOLLOW_UP";
  payload: {
    schema_version: 1;
    id: Uuid;
    content?: string | null;
    status?: AssistantFollowUpStatus | null;
    scheduled_for?: string | null;
    project_id?: Uuid | null;
    clear_project_id?: boolean;
    task_id?: Uuid | null;
    clear_task_id?: boolean;
    trigger_type?: string | null;
    commitment_kind?: string | null;
    due_at?: string | null;
    deadline_at?: string | null;
    trigger_not_before?: string | null;
    trigger_not_after?: string | null;
    fired_at?: string | null;
    completed_at?: string | null;
  };
};

export type IUpdateAssistantRoutineOperation = AssistantSyncOperationRequestShared & {
  operation_kind: "UPDATE_ASSISTANT_ROUTINE";
  payload: {
    schema_version: 1;
    id: Uuid;
    label?: string | null;
    description?: string | null;
    config_json?: Record<string, unknown> | null;
    status?: AssistantRoutineStatus | null;
    next_run_at?: string | null;
    clear_next_run_at?: boolean;
    last_run_at?: string | null;
  };
};

export type IDeleteAssistantRoutineOperation = AssistantSyncOperationRequestShared & {
  operation_kind: "DELETE_ASSISTANT_ROUTINE";
  payload: {
    schema_version: 1;
    id: Uuid;
  };
};

export type ICreateAssistantSkillOperation = AssistantSyncOperationRequestShared & {
  operation_kind: "CREATE_ASSISTANT_SKILL";
  payload: {
    schema_version: 1;
    id: Uuid;
    assistant_id: Uuid;
    name: string;
    when_to_use: string;
    instructions: string;
    origin: AssistantSkillOrigin;
  };
};

export type IUpdateAssistantSkillOperation = AssistantSyncOperationRequestShared & {
  operation_kind: "UPDATE_ASSISTANT_SKILL";
  payload: {
    schema_version: 1;
    id: Uuid;
    name?: string | null;
    when_to_use?: string | null;
    instructions?: string | null;
    status?: AssistantSkillStatus | null;
    sharing_enabled?: boolean | null;
  };
};

export type IDeleteAssistantSkillOperation = AssistantSyncOperationRequestShared & {
  operation_kind: "DELETE_ASSISTANT_SKILL";
  payload: {
    schema_version: 1;
    id: Uuid;
  };
};

// NOTE ATTACHMENT OPERATIONS
// Extends the OpenAPI item shape with the FE-driven optimistic-stub fields.
// `attachment_url`, `mime_type`, and `page_count` are BE-accepted upload hints
// (MEM-16898); `filename` is FE-only and silently dropped by the backend. All four
// live on the payload (not on a separate constructor arg) so they survive
// serialization to/from IndexedDB when the queue replays persisted ops via
// `deserializeSyncOperation`.
type BatchCreateSpaceAccountNoteAttachmentOperationBase =
  ApiSchema["SubmitSyncOperationRequestSyncModelCreateSpaceAccountNoteAttachmentSyncOperationPayloadRequest"];

export type BatchCreateSpaceAccountNoteAttachmentItem =
  BatchCreateSpaceAccountNoteAttachmentOperationBase["payload"]["note_attachments"][number] & {
    attachment_url: string;
    mime_type: string;
    filename: string;
    page_count: number | null;
    // FE-written hosting status — set for direct uploads; omitted for payloads
    // that don't carry one.
    hosting_status?: ApiSchema["NoteAttachmentHostingStatus"] | null;
  };

export type IBatchCreateSpaceAccountNoteAttachmentOperation = Omit<
  BatchCreateSpaceAccountNoteAttachmentOperationBase,
  "operation_kind" | "payload"
> & {
  operation_kind: "BATCH_CREATE_SPACE_ACCOUNT_NOTE_ATTACHMENT";
  payload: Omit<
    BatchCreateSpaceAccountNoteAttachmentOperationBase["payload"],
    "note_attachments"
  > & {
    note_attachments: BatchCreateSpaceAccountNoteAttachmentItem[];
  };
};

// FE-written hosting lifecycle (PENDING → READY/FAILED) for direct uploads. The
// BE op (PR #20546 / MEM-17408) accepts FE-supplied hosting fields per item and
// persists them — no reprocess, no round-trip. Wire payload is `{ items }`.
type BatchUpdateSpaceAccountNoteAttachmentHostingOperationBase =
  ApiSchema["SubmitSyncOperationRequestSyncModelBatchUpdateSpaceAccountNoteAttachmentHostingSyncOperationPayloadRequest"];

export type NoteAttachmentHostingItem =
  BatchUpdateSpaceAccountNoteAttachmentHostingOperationBase["payload"]["items"][number];

export type IBatchUpdateSpaceAccountNoteAttachmentHostingOperation = Omit<
  BatchUpdateSpaceAccountNoteAttachmentHostingOperationBase,
  "operation_kind"
> & {
  operation_kind: "BATCH_UPDATE_SPACE_ACCOUNT_NOTE_ATTACHMENT_HOSTING";
};

export type ISyncOperation =
  | ICreateNoteOperation
  | IUpdateNoteContentUsingDiffOperation
  | IUpdateNoteContentUsingFullStateOperation
  | IDeleteNoteOperation
  | ITrashNoteOperation
  | IRestoreNoteOperation
  | IUpdateNoteLocallyCreatedAtOperation
  | IUpdateNoteIsEditableByAiOperation
  | IGrantNoteAclViaSpaceAccountOperation
  | IGrantNoteAclViaEmailAddressOperation
  | IUpdateNoteAclViaSpaceAccountOperation
  | IUpdateNoteAclViaEmailAddressOperation
  | IUpdateNoteAclViaCollectionOperation
  | IRevokeNoteAclViaSpaceAccountOperation
  | IRevokeNoteAclViaEmailAddressOperation
  | ICreateCollectionOperation
  | IUpdateCollectionOperation
  | IDeleteCollectionOperation
  | IAddNoteToCollectionOperation
  | IRemoveNoteFromCollectionOperation
  | IGrantCollectionAclViaSpaceAccountOperation
  | IGrantCollectionAclViaEmailAddressOperation
  | IUpdateCollectionAclViaSpaceAccountOperation
  | IUpdateCollectionAclViaEmailAddressOperation
  | IRevokeCollectionAclViaSpaceAccountOperation
  | IRevokeCollectionAclViaEmailAddressOperation
  | IMakeCollectionPrivateOperation
  | IAddNoteToFavoritesOperation
  | IAddCollectionToFavoritesOperation
  | IAddSavedSearchToFavoritesOperation
  | IRemoveNoteFromFavoritesOperation
  | IRemoveCollectionFromFavoritesOperation
  | IRemoveSavedSearchFromFavoritesOperation
  | IUpdateFavoriteItemSortKeyOperation
  | IMarkNoteViewedOperation
  | IMarkCollectionViewedOperation
  | IProvideFeedbackOperation
  | IDismissSpaceAccountAppCalloutOperation
  | IEnableSpaceAccountAppCalloutDismissalOperation
  | ICreateApiKeyOperation
  | IUpdateApiKeyOperation
  | IDeleteApiKeyOperation
  | ICreateAudioRecordingOperation
  | IUpdateAudioRecordingOperation
  | ITrashAudioRecordingOperation
  | IRestoreAudioRecordingOperation
  | IDeleteAudioRecordingOperation
  | IAudioRecordingAppEventOperation
  | ICreateSessionOperation
  | IMarkSessionSeenOperation
  | ICreateSessionEventOperation
  | ICreateTemplateOperation
  | IUpdateTemplateOperation
  | IUpdateTemplateContentUsingDiffOperation
  | IDeleteTemplateOperation
  | IGrantTemplateAclViaEmailAddressOperation
  | IGrantTemplateAclViaSpaceAccountOperation
  | IUpdateTemplateAclViaSpaceAccountOperation
  | IRevokeTemplateAclViaSpaceAccountOperation
  | IBatchDeleteSourceOperation
  | IDeleteSourceOperation
  | IAddTemplateToCollectionOperation
  | IRemoveTemplateFromCollectionOperation
  | IUpdateTemplateAclViaCollectionOperation
  | ICreateNoteUsingTemplateOperation
  | IUpdateNoteContentUsingTemplateAndDiffOperation
  | ICreateNoteUsingCalendarEventOperation
  | ILinkNoteToCalendarEventOperation
  | IUpdateSpaceAccountCalendarEventOperation
  | IMarkTemplateViewedOperation
  | IMarkTemplateUsedOperation
  | ICreateDataExportOperation
  | ICreateUploadedFileBatchOperation
  | ICreateMessageSentSessionEventOperation
  | ICreateSourceOperation
  | ICreateNoteSourceOperation
  | IDeleteNoteSourceOperation
  | IUpdateSpaceAccountSettingsOperation
  | ICreateAssistantTaskOperation
  | IUpdateAssistantTaskOperation
  | ICreateAssistantProjectOperation
  | IUpdateAssistantProjectOperation
  | ICreateAssistantFollowUpOperation
  | IUpdateAssistantFollowUpOperation
  | IUpdateAssistantRoutineOperation
  | IDeleteAssistantRoutineOperation
  | ICreateAssistantSkillOperation
  | IUpdateAssistantSkillOperation
  | IDeleteAssistantSkillOperation
  | IBatchCreateSpaceAccountNoteAttachmentOperation
  | IBatchUpdateSpaceAccountNoteAttachmentHostingOperation;

// GUEST MODE OPERATIONS
export const GUEST_MODE_SUPPORTED_SYNC_OPERATIONS = [
  "UPDATE_NOTE_CONTENT_USING_DIFF",
  "GRANT_NOTE_ACL_VIA_EMAIL_ADDRESS",
  "UPDATE_NOTE_ACL_VIA_EMAIL_ADDRESS",
  "REVOKE_NOTE_ACL_VIA_EMAIL_ADDRESS",
] as const;

export type GuestModeSupportedSyncOperationKind =
  (typeof GUEST_MODE_SUPPORTED_SYNC_OPERATIONS)[number];

// TODO - Update when guest mode support is implemented
export type IUpdateNoteContentUsingDiffOperationGuestMode = IUpdateNoteContentUsingDiffOperation;
export type IGrantNoteAclViaEmailAddressOperationGuestMode = IGrantNoteAclViaEmailAddressOperation;
export type IUpdateNoteAclViaEmailAddressOperationGuestMode =
  IUpdateNoteAclViaEmailAddressOperation;
export type IRevokeNoteAclViaEmailAddressOperationGuestMode =
  IRevokeNoteAclViaEmailAddressOperation;

export type ISyncOperationGuestMode =
  | IUpdateNoteContentUsingDiffOperationGuestMode
  | IGrantNoteAclViaEmailAddressOperationGuestMode
  | IUpdateNoteAclViaEmailAddressOperationGuestMode
  | IRevokeNoteAclViaEmailAddressOperationGuestMode;

export enum ExternalOperationKind {
  UPLOAD_IMAGE = "UPLOAD_IMAGE",
  UPLOAD_FILE = "UPLOAD_FILE",
}

export type IUploadImageExternalOperation = {
  id: Uuid;
  client_id: string;
  locally_committed_at: string;
  operation_kind: ExternalOperationKind.UPLOAD_IMAGE;
  payload: {
    schema_version: 1;
    info: MemCommonEditorImageInfo;
    // The note this upload belongs to — needed to resolve the deterministic
    // attachment id for the FE-written hosting_status + LBS cache-on-insert.
    noteId: Uuid;
  };
};

export type IUploadFileExternalOperation = {
  id: Uuid;
  client_id: string;
  locally_committed_at: string;
  operation_kind: ExternalOperationKind.UPLOAD_FILE;
  payload: {
    schema_version: 1;
    info: MemCommonEditorFileInfo;
    // The note this upload belongs to — needed to resolve the deterministic
    // attachment id for the create the op now owns.
    noteId: Uuid;
    // PDF page count from validate-time; feeds the optimistic stub's
    // page_count / processing_info (and thus the PDF page quota accounting).
    pageCount?: number | null;
  };
};

export type IExternalOperation = IUploadImageExternalOperation | IUploadFileExternalOperation;

export type SyncOperation = BaseSyncOperation<ISyncOperation>;

export type SyncOperationGuestMode = BaseSyncOperationGuestMode<
  ISyncOperation,
  ISyncOperationGuestMode
>;

export type ExternalOperation = BaseExternalOperation<IExternalOperation>;

export type SyncOperationGeneric = BaseSyncOperationGeneric<
  ISyncOperation | ISyncOperationGuestMode | IExternalOperation
>;

export interface SerializedSyncOperation {
  operationKind: GenericSyncOperationKind;
  payload:
    | ISyncOperation["payload"]
    | ISyncOperationGuestMode["payload"]
    | IExternalOperation["payload"];
  operationId: string;
  committedAt: string;
  latestSpaceAccountSequenceId: MaybeNull<number>;
  modelId: MaybeNull<string>;
  noteId: MaybeNull<string>;
  collectionId: MaybeNull<string>;
  templateId: MaybeNull<string>;
  primaryLabel: MaybeNull<string>;
  secondaryLabel: MaybeNull<string>;
}
